import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState } from "react";
import { Loader2, Sparkles, Copy, Upload, FileText, CheckCircle2, AlertTriangle, Save, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  adminBuildModuleContent,
  adminSaveGeneratedModule,
  adminListPillarCourses,
  type BuiltModuleContent,
} from "@/lib/api/academy-content-builder.functions";

export const Route = createFileRoute("/_authenticated/portal/admin/academy-ai-builder")({
  component: AcademyAiBuilderPage,
});

const MAX_UPLOAD_BYTES = 12 * 1024 * 1024; // 12 MB
export const TRUNCATION_MARKER = "AI_GENERATION_TRUNCATED";

export function isTruncationError(err: unknown): boolean {
  const msg = (err as { message?: string })?.message ?? String(err ?? "");
  return msg.includes(TRUNCATION_MARKER);
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode(...bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function copy(text: string, label: string) {
  navigator.clipboard.writeText(text).then(
    () => toast.success(`${label} copied`),
    () => toast.error("Could not copy — select and copy manually"),
  );
}

function AcademyAiBuilderPage() {
  const [text, setText] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [audience, setAudience] = useState<
    "academy_adult" | "llb_undergraduate" | "high_school" | "professional_cpd"
  >("academy_adult");
  const [focus, setFocus] = useState("");
  const [quizCount, setQuizCount] = useState(8);
  const [debateCount, setDebateCount] = useState(3);
  const [result, setResult] = useState<BuiltModuleContent | null>(null);
  const [truncated, setTruncated] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const timerRef = useRef<number | null>(null);

  const build = useServerFn(adminBuildModuleContent);
  const mutation = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = {
        audience,
        quizCount,
        debateCount,
      };
      if (focus.trim()) payload.focus = focus.trim();
      if (text.trim().length >= 50) payload.text = text.trim();
      if (file) {
        if (file.size > MAX_UPLOAD_BYTES) {
          throw new Error(`Upload is ${(file.size / 1024 / 1024).toFixed(1)} MB — please keep under 12 MB.`);
        }
        payload.fileName = file.name;
        payload.fileMime = file.type || "application/octet-stream";
        payload.fileBase64 = await fileToBase64(file);
      }
      if (!payload.text && !payload.fileBase64) {
        throw new Error("Paste at least 50 characters of text or upload a document.");
      }
      return build({ data: payload as any });
    },
    onSuccess: (r) => {
      setResult(r.content);
      setTruncated(false);
      toast.success("Module content generated");
    },
    onError: (e: any) => {
      if (isTruncationError(e)) {
        setTruncated(true);
        toast.error("Generation was cut off — reduce size and retry");
      } else {
        setTruncated(false);
        toast.error(e?.message ?? "Generation failed");
      }
    },
  });

  useEffect(() => {
    if (mutation.isPending) {
      setElapsed(0);
      timerRef.current = window.setInterval(
        () => setElapsed((s) => s + 1),
        1000,
      ) as unknown as number;
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [mutation.isPending]);

  const retrySmaller = () => {
    setQuizCount((n) => Math.max(3, Math.floor(n / 2)));
    setDebateCount((n) => Math.max(1, Math.floor(n / 2)));
    setTruncated(false);
    setTimeout(() => mutation.mutate(), 30);
  };


  return (
    <div className="mx-auto max-w-6xl space-y-6 px-4 py-8">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-primary" />
          <h1 className="text-2xl font-semibold">Academy AI content builder</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Paste source material or upload a document (PDF, DOCX, images). The AI drafts a lesson,
          quiz, case study and debate prompts you can review and publish into any module.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Source material</CardTitle>
          <CardDescription>
            Use one or both inputs. Anything you provide grounds the generated content — the AI is
            told not to invent SA citations.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Audience</Label>
              <Select value={audience} onValueChange={(v: any) => setAudience(v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="academy_adult">NDLCS Academy — adult learners</SelectItem>
                  <SelectItem value="llb_undergraduate">UNISA LLB undergraduate</SelectItem>
                  <SelectItem value="high_school">High School legal literacy</SelectItem>
                  <SelectItem value="professional_cpd">Professional CPD</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Teaching focus (optional)</Label>
              <Input
                value={focus}
                maxLength={300}
                onChange={(e) => setFocus(e.target.value)}
                placeholder='e.g. "POPIA lawful bases for HR data"'
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Paste source text (min 50 characters)</Label>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste study notes, an Act extract, a policy, or reference material..."
              className="min-h-[180px]"
              maxLength={120_000}
            />
            <p className="text-xs text-muted-foreground">{text.length.toLocaleString()} / 120,000 characters</p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Upload className="h-4 w-4" /> Upload document (PDF, DOCX, TXT, MD, JPG, PNG, WEBP — up to 12 MB)
            </Label>
            <Input
              type="file"
              accept=".pdf,.docx,.doc,.txt,.md,image/jpeg,image/png,image/webp,image/gif"
              onChange={(e) => {
                const f = e.target.files?.[0] ?? null;
                if (!f) return setFile(null);
                if (f.size > MAX_UPLOAD_BYTES) {
                  toast.error(`File is ${(f.size / 1024 / 1024).toFixed(1)} MB — please keep it under 12 MB.`);
                  e.target.value = "";
                  return;
                }
                const okExt = /\.(pdf|docx?|txt|md)$/i.test(f.name) || f.type.startsWith("image/");
                if (!okExt) {
                  toast.error("Unsupported file type — upload PDF, DOCX, TXT, MD, or an image.");
                  e.target.value = "";
                  return;
                }
                setFile(f);
              }}
            />
            {file ? (
              <p className="flex items-center gap-2 text-xs text-muted-foreground">
                <FileText className="h-3 w-3" /> {file.name} — {(file.size / 1024).toFixed(0)} KB
              </p>
            ) : null}
          </div>


          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label>Quiz questions ({quizCount})</Label>
              <Input
                type="number"
                min={3}
                max={15}
                value={quizCount}
                onChange={(e) => setQuizCount(Math.max(3, Math.min(15, Number(e.target.value) || 8)))}
              />
            </div>
            <div className="space-y-2">
              <Label>Debate motions ({debateCount})</Label>
              <Input
                type="number"
                min={1}
                max={6}
                value={debateCount}
                onChange={(e) => setDebateCount(Math.max(1, Math.min(6, Number(e.target.value) || 3)))}
              />
            </div>
          </div>

          {mutation.isPending ? (
            <div className="space-y-1 rounded border bg-muted/40 p-3">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="flex items-center gap-2">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Generating — {elapsed}s elapsed (typical 20–70s)
                </span>
                <span>{Math.min(95, Math.round((elapsed / 90) * 100))}%</span>
              </div>
              <Progress value={Math.min(95, (elapsed / 90) * 100)} />
            </div>
          ) : null}

          {truncated && !mutation.isPending ? (
            <div className="flex items-start justify-between gap-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-none" />
                <div>
                  <p className="font-medium">Generation truncated</p>
                  <p className="text-xs">
                    The AI response was cut off before the JSON completed. Reduce the quiz/debate
                    count or shorten the source, then retry.
                  </p>
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={retrySmaller}>
                <RotateCw className="mr-2 h-3.5 w-3.5" /> Halve counts &amp; retry
              </Button>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3 border-t pt-4">
            <p className="text-xs text-muted-foreground">
              Review everything before publishing. AI can misstate SA law — verify citations.
            </p>
            <Button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              size="lg"
            >
              {mutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" /> Generate module content
                </>
              )}
            </Button>
          </div>

        </CardContent>
      </Card>

      {result ? <ResultView result={result} /> : null}
    </div>
  );
}

function ResultView({ result }: { result: BuiltModuleContent }) {
  const listPillars = useServerFn(adminListPillarCourses);
  const save = useServerFn(adminSaveGeneratedModule);
  const pillarsQ = useQuery({ queryKey: ["ai-pillars"], queryFn: () => listPillars() });
  const [pillar, setPillar] = useState<string>("");
  const [moduleTitle, setModuleTitle] = useState(result.lesson_title);
  const [ackCitations, setAckCitations] = useState(false);
  const [ackBias, setAckBias] = useState(false);
  const [ackDisclaimer, setAckDisclaimer] = useState(false);

  const saveMut = useMutation({
    mutationFn: () =>
      save({
        data: {
          course_slug: pillar,
          module_title: moduleTitle.trim(),
          content: result,
          compliance_ack: {
            reviewed_citations: ackCitations,
            reviewed_bias: ackBias,
            not_legal_advice: ackDisclaimer,
          },
        },
      }),
    onSuccess: (r) =>
      toast.success(`Saved as module #${r.orderIndex + 1} under ${pillar}`),
    onError: (e: any) => toast.error(e?.message ?? "Save failed"),
  });

  const canSave =
    pillar.length > 0 &&
    moduleTitle.trim().length >= 2 &&
    ackCitations &&
    ackBias &&
    ackDisclaimer &&
    !saveMut.isPending;

  return (
    <>
      <Card className="border-navy/30">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Save className="h-4 w-4" /> Publish to a pillar
          </CardTitle>
          <CardDescription>
            Store the generated lesson, quiz, case study and debates as a new module under the
            chosen NDLCS Academy pillar. All compliance checks must pass.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-1">
              <Label>Target pillar / course</Label>
              <Select value={pillar} onValueChange={setPillar}>
                <SelectTrigger>
                  <SelectValue placeholder={pillarsQ.isLoading ? "Loading…" : "Choose a pillar"} />
                </SelectTrigger>
                <SelectContent>
                  {(pillarsQ.data ?? []).map((c: any) => (
                    <SelectItem key={c.slug} value={c.slug}>
                      {c.category} — {c.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Module title</Label>
              <Input value={moduleTitle} onChange={(e) => setModuleTitle(e.target.value)} maxLength={200} />
            </div>
          </div>
          <div className="rounded border bg-muted/40 p-3 text-sm">
            <p className="font-medium">Compliance checklist</p>
            <label className="mt-2 flex items-start gap-2">
              <input type="checkbox" checked={ackCitations} onChange={(e) => setAckCitations(e.target.checked)} />
              <span>I have verified every SA statute, section and case citation in the lesson.</span>
            </label>
            <label className="mt-1 flex items-start gap-2">
              <input type="checkbox" checked={ackBias} onChange={(e) => setAckBias(e.target.checked)} />
              <span>Content is accurate, non-discriminatory, and age-appropriate for the selected learner level.</span>
            </label>
            <label className="mt-1 flex items-start gap-2">
              <input type="checkbox" checked={ackDisclaimer} onChange={(e) => setAckDisclaimer(e.target.checked)} />
              <span>Content is educational only and includes / preserves the "not legal advice" disclaimer.</span>
            </label>
          </div>
          <Button disabled={!canSave} onClick={() => saveMut.mutate()}>
            {saveMut.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Saving…
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" /> Save as module under pillar
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      <FullResultView result={result} />
    </>
  );
}

function FullResultView({ result }: { result: BuiltModuleContent }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" /> {result.lesson_title}
          </CardTitle>
          <CardDescription>{result.lesson_summary}</CardDescription>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => copy(JSON.stringify(result, null, 2), "Full JSON")}
        >
          <Copy className="mr-2 h-3.5 w-3.5" /> Copy all JSON
        </Button>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="lesson">
          <TabsList className="flex-wrap">
            <TabsTrigger value="lesson">Lesson</TabsTrigger>
            <TabsTrigger value="quiz">Quiz ({result.quiz.length})</TabsTrigger>
            <TabsTrigger value="case">Case study</TabsTrigger>
            <TabsTrigger value="debate">Debates ({result.debate_prompts.length})</TabsTrigger>
            <TabsTrigger value="terms">Key terms</TabsTrigger>
          </TabsList>

          <TabsContent value="lesson" className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => copy(result.lesson_markdown, "Lesson markdown")}>
                <Copy className="mr-2 h-3.5 w-3.5" /> Copy markdown
              </Button>
            </div>
            <pre className="max-h-[500px] overflow-auto whitespace-pre-wrap rounded bg-muted p-4 text-sm">
              {result.lesson_markdown}
            </pre>
          </TabsContent>

          <TabsContent value="quiz" className="space-y-3">
            <div className="flex justify-end">
              <Button size="sm" variant="ghost" onClick={() => copy(JSON.stringify(result.quiz, null, 2), "Quiz JSON")}>
                <Copy className="mr-2 h-3.5 w-3.5" /> Copy quiz JSON
              </Button>
            </div>
            <ol className="space-y-4">
              {result.quiz.map((q, i) => (
                <li key={i} className="rounded border p-3">
                  <p className="font-medium">
                    {i + 1}. {q.q}
                  </p>
                  <ul className="mt-2 space-y-1 text-sm">
                    {q.options.map((opt, oi) => (
                      <li
                        key={oi}
                        className={
                          oi === q.correct_index
                            ? "flex items-center gap-2 font-medium text-emerald-700"
                            : "flex items-center gap-2 text-muted-foreground"
                        }
                      >
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full border text-xs">
                          {String.fromCharCode(65 + oi)}
                        </span>
                        {opt}
                      </li>
                    ))}
                  </ul>
                  {q.explanation ? (
                    <p className="mt-2 text-xs text-muted-foreground">
                      <span className="font-semibold">Why:</span> {q.explanation}
                    </p>
                  ) : null}
                </li>
              ))}
            </ol>
          </TabsContent>

          <TabsContent value="case" className="space-y-3">
            {result.case_study ? (
              <>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold">{result.case_study.title}</h3>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => copy(JSON.stringify(result.case_study, null, 2), "Case study JSON")}
                  >
                    <Copy className="mr-2 h-3.5 w-3.5" /> Copy
                  </Button>
                </div>
                <p className="whitespace-pre-wrap rounded bg-muted p-3 text-sm">
                  {result.case_study.scenario}
                </p>
                <div>
                  <p className="text-sm font-medium">Discussion questions</p>
                  <ul className="ml-5 list-disc text-sm">
                    {result.case_study.discussion_questions.map((d, i) => (
                      <li key={i}>{d}</li>
                    ))}
                  </ul>
                </div>
                {result.case_study.teaching_notes ? (
                  <div className="rounded border-l-4 border-primary bg-primary/5 p-3 text-sm">
                    <p className="font-medium">Teaching notes</p>
                    <p className="mt-1 whitespace-pre-wrap">{result.case_study.teaching_notes}</p>
                  </div>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No case study generated.</p>
            )}
          </TabsContent>

          <TabsContent value="debate" className="space-y-3">
            {result.debate_prompts.map((d, i) => (
              <div key={i} className="rounded border p-3">
                <p className="font-medium">Motion: {d.motion}</p>
                <div className="mt-2 grid gap-3 sm:grid-cols-2">
                  <div>
                    <Badge variant="outline" className="mb-1 border-emerald-300 text-emerald-700">For</Badge>
                    <ul className="ml-4 list-disc text-sm">
                      {d.for_points.map((p, pi) => (
                        <li key={pi}>{p}</li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <Badge variant="outline" className="mb-1 border-rose-300 text-rose-700">Against</Badge>
                    <ul className="ml-4 list-disc text-sm">
                      {d.against_points.map((p, pi) => (
                        <li key={pi}>{p}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            ))}
          </TabsContent>

          <TabsContent value="terms" className="space-y-2">
            <dl className="space-y-2">
              {result.key_terms.map((t, i) => (
                <div key={i} className="rounded border p-2">
                  <dt className="font-medium">{t.term}</dt>
                  <dd className="text-sm text-muted-foreground">{t.definition}</dd>
                </div>
              ))}
            </dl>
          </TabsContent>
        </Tabs>

        {result.compliance_notes ? (
          <div className="mt-4 flex items-start gap-2 rounded border-l-4 border-amber-400 bg-amber-50 p-3 text-sm text-amber-900">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <p className="font-medium">Compliance notes</p>
              <p className="mt-1 whitespace-pre-wrap">{result.compliance_notes}</p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
